import { Canvas } from 'fabric';
import { useAtomValue } from 'jotai';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Spinner } from '@/shared/ui/atoms';

import { CANVAS_DIMENSIONS } from '../../../constants';
import { useSlideEditorContext } from '../../../context/SlideEditorContext';
import { useEditorUi } from '../../../hooks/useEditorUi';
import { slideAtom, type VariantKey } from '../../../store/atoms/slideAtom';
import { CanvasGuides } from '../../atoms/CanvasGuides';
import { ZoomControl } from '../../atoms/ZoomControl';

const {
  desktop: { width: DESKTOP_WIDTH, height: DESKTOP_HEIGHT },
} = CANVAS_DIMENSIONS;
const {
  mobile: { width: MOBILE_WIDTH, height: MOBILE_HEIGHT },
} = CANVAS_DIMENSIONS;

export function CanvasStage() {
  const ctx = useSlideEditorContext();
  const [uiState, setUiState] = useEditorUi();
  const [isDesktopReady, setIsDesktopReady] = useState(false);
  const [isMobileReady, setIsMobileReady] = useState(false);
  const [showGuides, setShowGuides] = useState(true);

  const desktopCanvasRef = useRef<HTMLCanvasElement>(null);
  const mobileCanvasRef = useRef<HTMLCanvasElement>(null);

  // Store Fabric instances in refs
  const fabricDesktopRef = useRef<Canvas | null>(null);
  const fabricMobileRef = useRef<Canvas | null>(null);

  // Initialize Desktop Canvas
  useEffect(() => {
    if (!desktopCanvasRef.current) return;

    const canvas = new Canvas(desktopCanvasRef.current, {
      width: DESKTOP_WIDTH,
      height: DESKTOP_HEIGHT,
      backgroundColor: '#141414', //Brand 900
      preserveObjectStacking: true,
    });

    fabricDesktopRef.current = canvas;
    ctx.onDesktopCanvasReady(canvas);
    setIsDesktopReady(true);

    return () => {
      canvas.off();
      void canvas.dispose();
      fabricDesktopRef.current = null;
      setIsDesktopReady(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.onDesktopCanvasReady]);

  // Initialize Mobile Canvas
  useEffect(() => {
    if (!mobileCanvasRef.current) return;

    const canvas = new Canvas(mobileCanvasRef.current, {
      width: MOBILE_WIDTH,
      height: MOBILE_HEIGHT,
      backgroundColor: '#141414', //Brand 900
      preserveObjectStacking: true,
    });

    fabricMobileRef.current = canvas;
    ctx.onMobileCanvasReady(canvas);
    setIsMobileReady(true);

    return () => {
      canvas.off();
      void canvas.dispose();
      fabricMobileRef.current = null;
      setIsMobileReady(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.onMobileCanvasReady]);

  const zoom = uiState.canvasZoom;
  const activeDevice = uiState.activeDevice;
  const currentWidth = activeDevice === 'desktop' ? DESKTOP_WIDTH : MOBILE_WIDTH;
  const currentHeight = activeDevice === 'desktop' ? DESKTOP_HEIGHT : MOBILE_HEIGHT;

  // Vídeo de fundo da variante ativa — renderizado atrás do canvas (transparente).
  const slide = useAtomValue(slideAtom);
  const variantKey: VariantKey = `${uiState.activeLanguage}-${activeDevice}`;
  const backgroundVideo = slide.variants[variantKey]?.backgroundVideo;
  // URL do vídeo cujo primeiro frame já está pronto. Mantém o loader enquanto o
  // <video> da nova variante bufferiza ao trocar de viewport — evita o "branco".
  const [readyVideoUrl, setReadyVideoUrl] = useState<string>();
  const isVideoLoading = !!backgroundVideo && readyVideoUrl !== backgroundVideo;

  // Apply zoom using Fabric's built-in API
  useEffect(() => {
    const desktopCanvas = fabricDesktopRef.current;
    const mobileCanvas = fabricMobileRef.current;

    if (desktopCanvas) {
      desktopCanvas.setZoom(zoom);
      desktopCanvas.setDimensions({
        width: DESKTOP_WIDTH * zoom,
        height: DESKTOP_HEIGHT * zoom,
      });
      desktopCanvas.renderAll();
    }

    if (mobileCanvas) {
      mobileCanvas.setZoom(zoom);
      mobileCanvas.setDimensions({
        width: MOBILE_WIDTH * zoom,
        height: MOBILE_HEIGHT * zoom,
      });
      mobileCanvas.renderAll();
    }
  }, [zoom]);

  const handleZoomIn = () =>
    setUiState(prev => ({ ...prev, canvasZoom: Math.min(prev.canvasZoom + 0.15, 3) }));
  const handleZoomOut = () =>
    setUiState(prev => ({ ...prev, canvasZoom: Math.max(prev.canvasZoom - 0.15, 0.3) }));
  const handleZoomReset = () => setUiState(prev => ({ ...prev, canvasZoom: 1 }));

  const containerRef = useRef<HTMLDivElement>(null);
  const handleZoomFit = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const { width: cw, height: ch } = container.getBoundingClientRect();
    const newZoom = Math.min(cw / currentWidth, ch / currentHeight) * 0.9;
    setUiState(prev => ({ ...prev, canvasZoom: Math.max(0.1, newZoom) }));
  }, [currentWidth, currentHeight, setUiState]);

  const isReady = activeDevice === 'desktop' ? isDesktopReady : isMobileReady;
  const showLoader = !isReady || ctx.isCanvasLoading || isVideoLoading;

  return (
    <div ref={containerRef} className="flex h-full w-full flex-col items-center gap-nano">
      <ZoomControl
        zoom={zoom}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onZoomReset={handleZoomReset}
        onZoomFit={handleZoomFit}
        showGuides={showGuides}
        onToggleGuides={() => setShowGuides(prev => !prev)}
      />

      <div
        className="relative overflow-hidden rounded-sm border-hairline border-ctx-layout-border shadow-level2"
        style={{
          width: currentWidth * zoom,
          height: currentHeight * zoom,
          transition: 'width 0.15s ease, height 0.15s ease',
        }}
      >
        {showLoader && (
          <div className="absolute inset-none z-20 flex items-center justify-center bg-ctx-layout-body">
            <Spinner size="md" />
          </div>
        )}

        {/* Vídeo de fundo: atrás do canvas (que fica transparente quando há vídeo). */}
        {backgroundVideo && (
          <video
            key={backgroundVideo}
            src={backgroundVideo}
            autoPlay
            loop
            muted
            playsInline
            onCanPlay={() => setReadyVideoUrl(backgroundVideo)}
            onError={() => setReadyVideoUrl(backgroundVideo)}
            className="absolute inset-none z-0 h-full w-full object-cover"
          >
            <track kind="captions" />
          </video>
        )}

        <CanvasGuides isMobile={activeDevice === 'mobile'} visible={showGuides && !showLoader} />

        {/* Desktop canvas */}
        <div
          className="relative z-[1]"
          style={{ display: activeDevice === 'desktop' ? 'block' : 'none' }}
        >
          <canvas
            ref={desktopCanvasRef}
            width={DESKTOP_WIDTH}
            height={DESKTOP_HEIGHT}
            style={{
              width: DESKTOP_WIDTH,
              height: DESKTOP_HEIGHT,
              display: 'block',
            }}
          />
        </div>

        {/* Mobile canvas */}
        <div
          className="relative z-[1]"
          style={{ display: activeDevice === 'mobile' ? 'block' : 'none' }}
        >
          <canvas
            ref={mobileCanvasRef}
            width={MOBILE_WIDTH}
            height={MOBILE_HEIGHT}
            style={{
              width: MOBILE_WIDTH,
              height: MOBILE_HEIGHT,
              display: 'block',
            }}
          />
        </div>
      </div>

      <span className="text-ctx-content-placeholder ts-caption-xxxs">
        {activeDevice === 'desktop'
          ? `${DESKTOP_WIDTH} × ${DESKTOP_HEIGHT}`
          : `${MOBILE_WIDTH} × ${MOBILE_HEIGHT}`}
      </span>
    </div>
  );
}
